import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import {
  Sprout, Wrench, MapPin, ShieldCheck, KanbanSquare, ClipboardList,
  Wallet, BarChart3, Scale, CalendarDays, ClipboardCheck, Bug
} from 'lucide-react';

export default function ProduccionLayout({ user, onLogout, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, isAdmin } = usePermissions(user);
  
  // Determinar la categoría activa basada en la ruta actual
  const [activeCategory, setActiveCategory] = useState('cosecha');

  // Tabs para Cosecha (todo lo existente)
  const cosechaTabs = [
    { name: 'Lotes y Parcelas', path: '/produccion', icon: MapPin, permission: 'agricultura.produccion.lotes.view' },
    { name: 'Seguridad y Salud Ocupacional', path: '/produccion/salud-seguridad', icon: ShieldCheck, permission: 'agricultura.produccion.salud_seguridad.view' },
    { name: 'Tablero de Tareas', path: '/produccion/tablero-tareas', icon: KanbanSquare, permission: 'agricultura.admin.tareas.view' },
    { name: 'Registro de Labores', path: '/produccion/registros', icon: ClipboardList, permission: 'agricultura.produccion.registros.view' },
    { name: 'Planilla', path: '/produccion/planilla', icon: Wallet, permission: 'agricultura.produccion.planilla.view' },
    { name: 'Reportes', path: '/produccion/reportes', icon: BarChart3, permission: 'agricultura.produccion.reportes.view' },
    { name: 'Hoja del Pesador', path: '/produccion/hoja-pesador', icon: Scale, permission: 'agricultura.produccion.hoja_pesador.view' },
    { name: 'Cronograma de Cosecha', path: '/produccion/cronograma', icon: CalendarDays, permission: 'agricultura.produccion.cronograma.view' },
    { name: 'Actas de Visitas', path: '/produccion/actas-visitas', icon: ClipboardCheck, permission: 'agricultura.produccion.actas_visitas.view' },
  ];

  // Tabs para Mantenimiento
  const mantenimientoTabs = [
    { name: 'Registro de Picudos', path: '/produccion/mantenimiento/picudos', icon: Bug, permission: 'agricultura.produccion.mantenimiento.view' },
  ];

  // Filtrar tabs según permisos
  const filteredCosechaTabs = isAdmin ? cosechaTabs : cosechaTabs.filter(tab => hasPermission(tab.permission));
  const filteredMantenimientoTabs = isAdmin ? mantenimientoTabs : mantenimientoTabs.filter(tab => hasPermission(tab.permission));

  // Verificar si el usuario tiene acceso a Mantenimiento
  const hasMantenimientoAccess = isAdmin || filteredMantenimientoTabs.length > 0;
  const hasCosechaAccess = isAdmin || filteredCosechaTabs.length > 0;

  // Calcular total de módulos accesibles
  const totalModulosAccesibles = filteredCosechaTabs.length + filteredMantenimientoTabs.length;
  
  // Si el usuario solo tiene acceso a UN módulo, mostrar vista simplificada (solo el contenido)
  const showSimplifiedView = !isAdmin && totalModulosAccesibles === 1;

  // Determinar categoría activa basada en la ruta
  useEffect(() => {
    if (location.pathname.includes('/produccion/mantenimiento')) {
      setActiveCategory('mantenimiento');
    } else {
      setActiveCategory('cosecha');
    }
  }, [location.pathname]);

  // Obtener tabs activas según la categoría
  const activeTabs = activeCategory === 'cosecha' ? filteredCosechaTabs : filteredMantenimientoTabs;

  // Obtener la primera ruta disponible para el usuario en Cosecha
  const getFirstAvailableCosechaRoute = () => {
    if (filteredCosechaTabs.length > 0) {
      return filteredCosechaTabs[0].path;
    }
    return '/produccion';
  };

  // Obtener la primera ruta disponible para el usuario en Mantenimiento
  const getFirstAvailableMantenimientoRoute = () => {
    if (filteredMantenimientoTabs.length > 0) {
      return filteredMantenimientoTabs[0].path;
    }
    return '/produccion/mantenimiento/picudos';
  };

  // Manejar cambio de categoría
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    if (category === 'cosecha') {
      navigate(getFirstAvailableCosechaRoute());
    } else {
      navigate(getFirstAvailableMantenimientoRoute());
    }
  };

  // Si es vista simplificada, solo mostrar el contenido sin navegación
  if (showSimplifiedView) {
    return (
      <div className="py-4">
        {children}
      </div>
    );
  }

  return (
    <div>
      {/* Categorías principales: Cosecha / Mantenimiento */}
      <div className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-4">
            {hasCosechaAccess && (
              <button
                onClick={() => handleCategoryChange('cosecha')}
                className={`
                  flex items-center px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap
                  ${activeCategory === 'cosecha'
                    ? 'text-emerald-600 border-b-2 border-emerald-600'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }
                `}
              >
                <Sprout className="w-4 h-4 mr-2" />
                Cosecha
              </button>
            )}
            {hasMantenimientoAccess && (
              <button
                onClick={() => handleCategoryChange('mantenimiento')}
                className={`
                  flex items-center px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap
                  ${activeCategory === 'mantenimiento'
                    ? 'text-emerald-600 border-b-2 border-emerald-600'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }
                `}
              >
                <Wrench className="w-4 h-4 mr-2" />
                Mantenimiento
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Sub-tabs según la categoría activa */}
      {activeCategory === 'cosecha' && (
        <div className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Navegación responsive: scroll horizontal automático cuando desborda */}
            <nav className="mobile-tab-scroll -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
              <div className="flex gap-1 sm:gap-4 min-w-max">
                {filteredCosechaTabs.map((tab) => {
                  const isActive = location.pathname === tab.path;
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.path}
                      onClick={() => navigate(tab.path)}
                      className={`
                        flex items-center gap-1.5 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap shrink-0
                        ${isActive
                          ? 'text-emerald-600 border-b-2 border-emerald-600'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border-b-2 border-transparent'
                        }
                      `}
                      data-testid={`cosecha-tab-${tab.path.split('/').pop() || 'lotes'}`}
                    >
                      {Icon && <Icon className="w-4 h-4 shrink-0" />}
                      {tab.name}
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>
        </div>
      )}

      {/* Sub-tabs de Mantenimiento */}
      {activeCategory === 'mantenimiento' && (
        <div className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="mobile-tab-scroll -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
              <div className="flex gap-1 sm:gap-4 min-w-max">
                {filteredMantenimientoTabs.map((tab) => {
                  const isActive = location.pathname === tab.path;
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.path}
                      onClick={() => navigate(tab.path)}
                      className={`
                        flex items-center gap-1.5 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap shrink-0
                        ${isActive
                          ? 'text-emerald-600 border-b-2 border-emerald-600'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border-b-2 border-transparent'
                        }
                      `}
                      data-testid={`mantenimiento-tab-${tab.path.split('/').pop()}`}
                    >
                      {Icon && <Icon className="w-4 h-4 shrink-0" />}
                      {tab.name}
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="dark:text-white">
        {children}
      </div>
    </div>
  );
}
